// KPIs surface (N-BOARD-2), the "Signal" re-layout (KPI Screen v2): an OVERVIEW GRID of KPI cards behind
// a left filter rail (lifecycle chips with honest counts + the "Waiting on you" nudge), and a full-width
// DETAIL view per Metric (headline, trend chart, closed-loop Task list, the Value EVIDENCE on demand
// G-011, and the confirm/activate gate). Labelled "KPIs" for users; the entity is Metric (AC-5 — every
// id/route uses Metric). Every card figure, count, and status derives from the projection (G-182); the
// chips, the selected view, and the selected Metric are client view-state only. The design's area
// groupings, sub-KPIs, and source stamps have no domain counterpart, so none render (P-SELF).
//
// Activation / value-approval are Fence-crossing Gates owned by `metrics` (E): the surface ROUTES the
// authenticated human intent via the Stage-1 POST route and re-reads the recorded state — it never writes
// an activation/authoritative approval field (P-GATE, AC-6). Thin over services; tokens only (G-181).

import { useCallback, useState } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useProjection } from './useProjection.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { KpiCard } from './components/KpiCard.tsx';
import { MetricDetail } from './components/MetricDetail.tsx';
import { EmptyState } from './primitives.tsx';
import { ScreenToolbar, ScreenBody, TbStamp, TbInfo } from '../app/screen-chrome.tsx';
import { Icon } from '../app/Icon.tsx';
import { useShell } from '../app/ShellContext.tsx';
import { fmtMetricValue, fmtDayMonth, isAwaiting } from './components/metric-format.ts';
import { flattenBoard } from './components/metric-wire.ts';
import type {
  MetricsBoardWire,
  MetricCard as MetricCardView,
  MetricEvidenceSheet,
  ApprovalGateKind,
} from './projections.ts';
import type { RouteResult } from './api-client.ts';

/** Stable write routes; every successful write re-reads the owner projection. */
function useMetricRoutes(
  api: ReturnType<typeof useSurfaceApi>,
  reload: () => void,
  clearSelection: () => void,
) {
  const onExercise = useCallback(
    async (kind: ApprovalGateKind, objectId: string) => {
      const result = await api.exerciseGate(kind, objectId, null);
      if (result.ok) reload();
      return result;
    },
    [api, reload],
  );
  const onDelete = useCallback(
    async (metricId: string) => {
      const result = await api.deleteMetricProposal(metricId);
      if (result.ok) {
        clearSelection();
        reload();
      }
      return result;
    },
    [api, clearSelection, reload],
  );
  // Collect now (BUILD-DEC-150): re-reads on ok so the board shows the RECORDED proposed Value, never an
  // optimistic one (G-182). A refusal returns the API's sanitised note for the detail rail to surface.
  const onCollectNow = useCallback(
    async (metricId: string) => {
      const result = await api.collectMetricNow(metricId);
      if (result.ok) reload();
      return result;
    },
    [api, reload],
  );
  return { onExercise, onDelete, onCollectNow };
}

/** The overview filter chips — lifecycle facts only (the design's area groups have no domain field). */
type BoardFilter = 'all' | 'needs' | 'proposed' | 'active';
const FILTERS: readonly { readonly id: BoardFilter; readonly label: string }[] = [
  { id: 'all', label: 'Everything' },
  { id: 'needs', label: 'Needs you' },
  { id: 'proposed', label: 'Proposed' },
  { id: 'active', label: 'Active' },
];

/** True when a Metric belongs under one filter chip (derived from lifecycle fields only, G-182). */
function inFilter(metric: MetricCardView, filter: BoardFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'needs') return isAwaiting(metric);
  return metric.state === filter;
}

/** The KPIs toolbar: the honest tracked/awaiting stamp, the New-KPI handoff, the info tip. */
function KpisToolbar({
  tracked,
  awaiting,
  showNew,
  onNewKpi,
}: {
  readonly tracked: number;
  readonly awaiting: number;
  readonly showNew: boolean;
  readonly onNewKpi: () => void;
}): React.JSX.Element {
  return (
    <>
      <TbStamp>
        {tracked} tracked · {awaiting} awaiting you
      </TbStamp>
      {showNew ? (
        <button type="button" className="tb-new" onClick={onNewKpi} aria-label="New KPI">
          <Icon name="plus" size={13} /> New KPI
        </button>
      ) : null}
      <TbInfo
        title="KPIs"
        tip="Each metric carries its definition and latest value, and shows the tasks moving it. Confirming a proposed value is a gate only you hold."
      />
    </>
  );
}

/** The KPIs (Metrics) board surface — the Signal overview grid + per-Metric detail view. */
export function KpisSurface(): React.JSX.Element {
  const api = useSurfaceApi();
  const shell = useShell();
  const state = useProjection<MetricsBoardWire>(api.getMetricsBoard);
  const reload = state.reload;
  const [filter, setFilter] = useState<BoardFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const clearSelection = useCallback(() => setSelectedId(null), []);
  const routes = useMetricRoutes(api, reload, clearSelection);
  const newKpi = useCallback(
    () =>
      shell.openNewChatWithDraft('Propose a new KPI. What I want to measure, in plain English: '),
    [shell],
  );

  const loadedMetrics = state.data !== null ? flattenBoard(state.data).metrics : [];
  const awaitingCount = loadedMetrics.filter(isAwaiting).length;

  return (
    <>
      <ScreenToolbar title="KPIs">
        <KpisToolbar
          tracked={loadedMetrics.length}
          awaiting={awaitingCount}
          showNew={selectedId === null}
          onNewKpi={newKpi}
        />
      </ScreenToolbar>
      <ScreenBody>
        <SurfaceFrame state={state}>
          {(wire) => (
            <KpisBody
              metrics={flattenBoard(wire).metrics}
              filter={filter}
              onFilter={setFilter}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onBack={clearSelection}
              onNewKpi={newKpi}
              loadEvidence={api.getMetricEvidence}
              onExercise={routes.onExercise}
              onDelete={routes.onDelete}
              onCollectNow={routes.onCollectNow}
            />
          )}
        </SurfaceFrame>
      </ScreenBody>
    </>
  );
}

/** The "Waiting on you" rail nudge — the FIRST awaiting Metric, stated from its recorded fields. */
function WaitingNudge({
  metric,
  onReview,
}: {
  readonly metric: MetricCardView;
  readonly onReview: () => void;
}): React.JSX.Element {
  const value = metric.latestValue;
  const body =
    metric.state === 'proposed'
      ? `${metric.name} is proposed and ${metric.activationReadiness?.ready === true ? 'awaiting activation' : 'needs setup'}.`
      : value !== null
        ? `${metric.name} ${fmtMetricValue(value.value)}, proposed for the period to ${fmtDayMonth(value.periodEnd)}.`
        : `${metric.name} is awaiting you.`;
  return (
    <div className="kr-nudge">
      <div className="kr-nudge-label">Waiting on you</div>
      <div className="kr-nudge-body">{body}</div>
      <button
        type="button"
        className="kr-nudge-go"
        onClick={onReview}
        aria-label={`Review KPI: ${metric.name}`}
      >
        Review it
      </button>
    </div>
  );
}

/** The dashed New-KPI tile at the end of the grid — a chat handoff, never a client-side write. */
function NewKpiTile({ onNewKpi }: { readonly onNewKpi: () => void }): React.JSX.Element {
  return (
    <button type="button" className="kpi-new" onClick={onNewKpi} aria-label="New KPI">
      <span className="kn-plus" aria-hidden="true">
        <Icon name="plus" size={20} />
      </span>
      <span className="kn-title">New KPI</span>
      <span className="kn-note">
        Tell Ritona what you want to measure in plain English — it works out how to collect it.
      </span>
    </button>
  );
}

/** The left filter rail: lifecycle chips with honest counts + the awaiting nudge when one exists. */
function FilterRail({
  metrics,
  filter,
  onFilter,
  onSelect,
}: {
  readonly metrics: readonly MetricCardView[];
  readonly filter: BoardFilter;
  readonly onFilter: (filter: BoardFilter) => void;
  readonly onSelect: (id: string) => void;
}): React.JSX.Element {
  const firstAwaiting = metrics.find(isAwaiting);
  return (
    <aside className="kpi-rail" aria-label="KPI filters">
      {FILTERS.map((chip) => {
        const count = metrics.filter((metric) => inFilter(metric, chip.id)).length;
        const on = filter === chip.id;
        return (
          <button
            key={chip.id}
            type="button"
            className="kr-chip"
            aria-pressed={on}
            data-filter={chip.id}
            onClick={() => onFilter(chip.id)}
          >
            <span>{chip.label}</span>
            <span className="cnt">{count}</span>
          </button>
        );
      })}
      {firstAwaiting !== undefined ? (
        <WaitingNudge metric={firstAwaiting} onReview={() => onSelect(firstAwaiting.id)} />
      ) : null}
    </aside>
  );
}

/** The loaded KPIs body — the overview grid behind the filter rail, or the selected detail view. */
function KpisBody({
  metrics,
  filter,
  onFilter,
  selectedId,
  onSelect,
  onBack,
  onNewKpi,
  loadEvidence,
  onExercise,
  onDelete,
  onCollectNow,
}: {
  readonly metrics: readonly MetricCardView[];
  readonly filter: BoardFilter;
  readonly onFilter: (filter: BoardFilter) => void;
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
  readonly onBack: () => void;
  readonly onNewKpi: () => void;
  readonly loadEvidence: (valueId: string) => Promise<MetricEvidenceSheet>;
  readonly onExercise: (kind: ApprovalGateKind, objectId: string) => Promise<RouteResult>;
  readonly onDelete: (metricId: string) => Promise<RouteResult>;
  readonly onCollectNow: (metricId: string) => Promise<RouteResult>;
}): React.JSX.Element {
  const selected = metrics.find((metric) => metric.id === selectedId) ?? null;
  if (selected !== null) {
    return (
      <MetricDetail
        metric={selected}
        onBack={onBack}
        loadEvidence={loadEvidence}
        onExercise={onExercise}
        onDelete={onDelete}
        onCollectNow={onCollectNow}
      />
    );
  }
  return (
    <KpisOverview
      metrics={metrics}
      filter={filter}
      onFilter={onFilter}
      onSelect={onSelect}
      onNewKpi={onNewKpi}
    />
  );
}

/** The overview: the filter rail + the card grid (+ the New-KPI tile), or the honest empty lines. */
function KpisOverview({
  metrics,
  filter,
  onFilter,
  onSelect,
  onNewKpi,
}: {
  readonly metrics: readonly MetricCardView[];
  readonly filter: BoardFilter;
  readonly onFilter: (filter: BoardFilter) => void;
  readonly onSelect: (id: string) => void;
  readonly onNewKpi: () => void;
}): React.JSX.Element {
  const shown = metrics.filter((metric) => inFilter(metric, filter));
  return (
    <div className="kpi-wrap">
      <FilterRail metrics={metrics} filter={filter} onFilter={onFilter} onSelect={onSelect} />
      <div className="kpi-grid-pane">
        {metrics.length === 0 ? (
          <EmptyState>No KPIs are defined yet.</EmptyState>
        ) : shown.length === 0 ? (
          <EmptyState>No KPI matches this filter.</EmptyState>
        ) : null}
        <div className="kpi-grid">
          {shown.map((metric) => (
            <KpiCard key={metric.id} metric={metric} onOpen={onSelect} />
          ))}
          <NewKpiTile onNewKpi={onNewKpi} />
        </div>
      </div>
    </div>
  );
}
